/** @format */

import { AxiosResponse } from "axios";
import { IValidationResult } from "lobbie";
import { useCallback, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { IAppointmentBookingResponse } from "src/components/patients/scheduling/types";
import FullScreenLoadingSpinner from "src/components/shared/loading/FullScreenLoadingSpinner";
import { ACCOUNT_PUBLIC_UUID, BROWSER_TIME_ZONE, DATE_TIME_FORMATS, ROUTES } from "src/constants";
import { usePatientSelfSchedulingContext } from "src/hooks/patient_scheduling/usePatientSelfSchedulingContext";

import { useAxiosPoster } from "src/hooks/useAxios";
import {
    daysjsFromUnix,
    handleError,
    isEmptyObject,
    isFailedRequest,
    logDev,
    notify,
    toThreeLinesAddressStrings,
} from "src/utils";

const PatientSchedulingConfirmation: React.FC = () => {
    const poster = useAxiosPoster();

    const { selfSchedule, setSelfSchedule, selfScheduleSettings, setRoute } =
        usePatientSelfSchedulingContext();

    const [isLoading, setLoading] = useState<boolean>(false);

    const accountPatientId = useMemo(
        () => selfSchedule?.accountPatient?.id,
        [selfSchedule?.accountPatient],
    );
    const selectedAccountPatientId = useMemo(
        () => selfSchedule?.selectedAccountPatient?.id,
        [selfSchedule?.selectedAccountPatient],
    );

    const timeZone = useMemo(() => BROWSER_TIME_ZONE, []);
    const startDayjs = useMemo(
        () =>
            selfSchedule?.time && timeZone
                ? daysjsFromUnix(selfSchedule.time, timeZone)
                : undefined,
        [selfSchedule?.time, timeZone],
    );

    const practitionerName = useMemo(() => {
        if (!selfScheduleSettings?.practitioners || !selfSchedule) return;
        logDev(
            "selfSchedule.practitionerId",
            selfScheduleSettings.practitioners,
            selfSchedule.practitionerId,
        );
        for (const practitioner of selfScheduleSettings.practitioners) {
            if (practitioner.practitionerId === selfSchedule.practitionerId) {
                return practitioner.practitionerName;
            }
        }
    }, [selfSchedule, selfScheduleSettings?.practitioners]);

    const handleSubmit = useCallback(() => {
        //if (!account) return;
        if (!selfScheduleSettings) return;
        if (!startDayjs) return;
        if (!selfSchedule?.location) return;
        if (!selfSchedule?.appointmentType) return;
        if (!selectedAccountPatientId && !accountPatientId) return;
        if (!selfSchedule?.practitionerId) return;
        setLoading(true);

        const locationTimeZone = selfSchedule.location.timeZone;
        const startDayjsInLocationTZ = startDayjs.tz(locationTimeZone);
        const startUnix = startDayjsInLocationTZ.unix();

        const data = {
            [ACCOUNT_PUBLIC_UUID]: selfScheduleSettings.accountPublicUuid,
            trackingUuid: selfScheduleSettings.trackingUuid,
            practitionerId: selfSchedule.practitionerId,
            locationId: selfSchedule.location.id,
            appointmentTypeId: selfSchedule.appointmentType.id,
            startUnix,
            isNotifyEarlierAppointment: selfSchedule.isNotifyEarlierAppointment,
            // @Deprecated TODO: DEPRECATED appointmentDate and appointmentTime are only for the old way of scheduling
            appointmentDate: startDayjs
                .tz(selfSchedule.location.timeZone)
                .format(DATE_TIME_FORMATS.Appointment.DateFormat),
            // Crucially, we change the timeZone here to the timeZone of the Location
            // We use BROWSER_TIME_ZONE for presenting the available times to the Patient,
            // but the backend requires the time of the Appointment to be "translated" to the time in the Location
            appointmentTime: startDayjs
                .tz(selfSchedule.location.timeZone)
                .format(DATE_TIME_FORMATS.Appointment.TimeFormat)
                .toUpperCase(),
            ...(selfSchedule.schedulerConfigId && {
                schedulerConfigId: selfSchedule.schedulerConfigId,
            }),
            ...(selfSchedule.conversionTrackingUuid && {
                conversionTrackingUuid: selfSchedule.conversionTrackingUuid,
            }),
        };

        if (selectedAccountPatientId || accountPatientId) {
            (data as Record<string, any>)["accountPatientId"] =
                selectedAccountPatientId || accountPatientId;
        }

        poster("/patientscheduling-v2/confirm-self-scheduled-appointment", data)
            .then((response: AxiosResponse | void) => {
                setLoading(false);

                const result =
                    response &&
                    (response.data as IAppointmentBookingResponse[] | IValidationResult);
                if (!response || !result) {
                    // 4.3.13
                    // expire().catch(console.error);
                } else if (isFailedRequest(result)) {
                    notify({
                        level: "error",
                        title: "Failed to create appointment.",
                        message: (result as IValidationResult).message,
                    });
                } else if (!isEmptyObject(result as IAppointmentBookingResponse[])) {
                    setSelfSchedule({
                        ...selfSchedule,
                        appointments: result as IAppointmentBookingResponse[],
                    });
                    setRoute(ROUTES.patientSchedulingBookingConfirmed);
                } else {
                    notify({
                        level: "error",
                        title: "Failed to create appointment",
                        message:
                            "Please wait a few seconds and try again, or, if you began scheduling this appointment a while ago your time may have expired and you may need to begin again.",
                    });
                }
            })
            .catch((error) => {
                handleError(error);
                setLoading(false);
            });
    }, [
        selfScheduleSettings,
        startDayjs,
        selfSchedule,
        selectedAccountPatientId,
        accountPatientId,
        poster,
        setSelfSchedule,
        setRoute,
    ]);
    if (!selfScheduleSettings || !selfSchedule) return null;
    if (!selfSchedule?.time || !selfSchedule?.date || !location) return null;

    if (isLoading) {
        return <FullScreenLoadingSpinner message={["Finishing your booking..."]} />;
    }

    return (
        <div className="col">
            <div className="row mb-2">
                <div className="col">
                    <h3 className="extra-bold mt-4">
                        Confirm <br />
                        booking details
                    </h3>
                </div>
            </div>
            <div className="row mt-3">
                <div className="col">
                    <div className="col border rounded p-3">
                        <span className="gray2">
                            {selfScheduleSettings?.appointmentPatientFieldName}:
                        </span>
                        <br />
                        <span className="semi-bold">{selfSchedule.appointmentType?.name}</span>
                    </div>

                    <div className="col border rounded p-3 mt-3">
                        <span className="gray2">Location:</span>
                        <br />
                        {selfSchedule.appointmentType?.telehealth ? (
                            <span className="semi-bold">{"Telehealth"}</span>
                        ) : (
                            <>
                                <span className="semi-bold">{selfSchedule.location?.name}</span>
                                <br />
                                <span className="semi-bold">
                                    {selfSchedule.location &&
                                        toThreeLinesAddressStrings(
                                            selfSchedule?.location.address,
                                        ).join(" ")}
                                </span>
                            </>
                        )}
                    </div>

                    <div className="col border rounded p-3 mt-3">
                        <span className="gray2">Seeing:</span>
                        <br />
                        <span className="semi-bold">{practitionerName}</span>
                    </div>
                    <div className="col border rounded p-3 mt-3">
                        <span className="gray2">Date &amp; Time:</span>
                        <br />
                        <span className="semi-bold">
                            {startDayjs?.format(DATE_TIME_FORMATS.Appointment.DateFormat2) || ""}
                            ,&nbsp;
                            {startDayjs
                                ?.tz(BROWSER_TIME_ZONE)
                                .format(DATE_TIME_FORMATS.Appointment.PatientHomeTime) || ""}
                        </span>
                    </div>
                    <div className="col border rounded p-3 mt-3">
                        <span className="gray2">{selfScheduleSettings?.patientFieldName}:</span>
                        <br />
                        <span className="semi-bold">
                            {selfSchedule.selectedAccountPatient?.name ||
                                selfSchedule.accountPatient?.name}
                        </span>
                    </div>
                </div>
            </div>
            <div className="row position-sticky fixed-bottom p-3 border-top bg-white mt-3">
                <div className="col my-4">
                    <div className="row">
                        <div className="col">
                            <Button
                                id="continue-btn"
                                className="w-100"
                                onClick={handleSubmit}
                                variant="primary"
                            >
                                <span>Confirm Booking</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PatientSchedulingConfirmation;
